import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert
} from "@mui/material";
import axios from "axios";

function DetailsModal({ open, onClose, job }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!job) return;

    const userIds = job.requestPayload?.usersToNotify?.map(u => u.userId) || [];
    if (userIds.length === 0) {
      setUsers([]);
      setLoading(false);
      return;
    }

    async function fetchUsers() {
      try {
        const responses = await Promise.all(
          userIds.map(id => axios.get(`/api/v1/userdetails/${id}`))
        );
        const usersData = responses.map(res => res.data.user);
        setUsers(usersData);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, [job]);

  if (!job) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, boxShadow: 24 } }}
    >
      <DialogTitle sx={{ 
        backgroundColor: 'primary.main', 
        color: 'white', 
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        Job Details (ID: {job.id})
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Type</Typography>
            <Typography variant="h7">{job.type}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Status</Typography>
            <Chip 
              label={job.status} 
              color={
                job.status === 'completed' ? 'success' : 
                job.status === 'failed' ? 'error' : 'primary'
              } 
              variant="outlined"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Users:
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : users.length === 0 ? (
          <Alert severity="info" sx={{ my: 2 }}>No users found for this notification job</Alert>
        ) : (
          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ 
              mt: 2, 
              borderRadius: 1,
              maxHeight: 400,
              overflow: 'auto'
            }}
          >
            <Table stickyHeader sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2',color:'#fff' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2' ,color:'#fff'}}>Username</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2',color:'#fff' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2',color:'#fff' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2',color:'#fff' }}>Subscription Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow 
                    key={u.id}
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell component="th" scope="row">{u.id}</TableCell>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={u.role} 
                        size="small" 
                        color={u.role === 'admin' ? 'secondary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={u.isUnsubscribed ? "Unsubscribed" : "Subscribed"} 
                        size="small"
                        color={u.isUnsubscribed ? "error" : "success"}
                        variant={u.isUnsubscribed ? "outlined" : "filled"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ 
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 'bold'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DetailsModal;